import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Settings, AlertTriangle } from 'lucide-react';

export const CaseSLAPanel = ({ isAdmin }) => {
  const [slaData, setSlaData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [yellowH, setYellowH] = useState(48);
  const [redH, setRedH] = useState(120);

  useEffect(() => {
    loadSLA();
  }, []);

  const loadSLA = async () => {
    try {
      const res = await api.get('/cases/sla-stats');
      setSlaData(res.data);
      setYellowH(res.data.config?.yellow_hours || 48);
      setRedH(res.data.config?.red_hours || 120);
    } catch (err) {
      console.error('Error loading SLA', err);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    try {
      await api.put('/cases/sla-config', { yellow_hours: parseInt(yellowH), red_hours: parseInt(redH) });
      toast.success('SLA actualizado');
      setShowConfig(false);
      loadSLA();
    } catch (err) {
      toast.error('Error al guardar SLA');
    }
  };

  if (loading || !slaData) return null;

  const { verde, amarillo, rojo } = slaData;

  return (
    <>
      <Card className="p-4 border border-slate-200 mb-4" data-testid="case-sla-panel">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Clock className="h-4 w-4" /> SLA de Casos
            <span className="text-xs font-normal text-slate-400">
              (Amarillo: {slaData.config?.yellow_hours}h | Rojo: {slaData.config?.red_hours}h)
            </span>
          </h3>
          {isAdmin && (
            <Button variant="ghost" size="sm" onClick={() => setShowConfig(true)} data-testid="sla-config-btn">
              <Settings className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-emerald-50 rounded-lg p-3 border border-emerald-200">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
              <span className="text-xs text-emerald-700 font-medium">En tiempo</span>
            </div>
            <p className="text-2xl font-bold text-emerald-800" data-testid="sla-green">{verde.count}</p>
          </div>
          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-xs text-amber-700 font-medium">Por vencer</span>
            </div>
            <p className="text-2xl font-bold text-amber-800" data-testid="sla-yellow">{amarillo.count}</p>
            {amarillo.count > 0 && amarillo.cases?.[0] && (
              <p className="text-xs text-amber-600 mt-1 truncate">
                Mas antiguo: {amarillo.cases[0].dias_abierto}d
              </p>
            )}
          </div>
          <div className={`rounded-lg p-3 border ${rojo.count > 0 ? 'bg-red-50 border-red-300 ring-1 ring-red-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-2.5 h-2.5 rounded-full bg-red-500 ${rojo.count > 0 ? 'animate-pulse' : ''}`} />
              <span className="text-xs text-red-700 font-medium">Vencidos</span>
            </div>
            <p className="text-2xl font-bold text-red-800" data-testid="sla-red">{rojo.count}</p>
            {rojo.count > 0 && rojo.cases?.[0] && (
              <p className="text-xs text-red-600 mt-1 truncate flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> {rojo.cases[0].dias_abierto}d abierto
              </p>
            )}
          </div>
        </div>
      </Card>

      <Dialog open={showConfig} onOpenChange={setShowConfig}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Configurar SLA</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm text-slate-600">Umbral amarillo (horas)</label>
              <Input type="number" value={yellowH} onChange={e => setYellowH(e.target.value)} data-testid="sla-yellow-input" />
            </div>
            <div>
              <label className="text-sm text-slate-600">Umbral rojo (horas)</label>
              <Input type="number" value={redH} onChange={e => setRedH(e.target.value)} data-testid="sla-red-input" />
            </div>
            <Button onClick={saveConfig} className="w-full" data-testid="sla-save-btn">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
